import { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import PageContext from "../../../components/Common/PageContext";
import {
	isThereAnyPoLineAbleToBeReopened,
	hasReceiveAlreadyBeenAllocatedCustomerOrders,
	receiveStatus,
	alerts,
	modals,
	mapPurchaseOrderLineItemsWithAdditionalDetails,
	enrichReceiveForManualReceive,
	mapLocationBaysFromReceiveLineItems,
	sortItemsByPriorityStockFlag,
	addOrUpdateReceiveToReceivesList,
} from "../utils/receiveUtilExports";
import {
	modalClosed,
	loadingDialogClosed,
	Modal,
	LoadingDialog,
	emptyCurrentScannedProduct,
	CurrentScannedProduct,
} from "../../../util/emptyObjectsUtil";
import { getAdditionalProductDetails } from "../../../util/ProductUtilities";
import { useGetReceivePurchaseOrderDetails } from "../api/useGetReceivePurchaseOrderDetails";
import { useHistory } from "react-router-dom";
import { AlertState } from "../../../util/alertUtil";
import { useFeatureCheck } from "../../../hooks/useFeatureCheck";
import useQuery from "../../../useQuery";
import { Receive } from "../../../common/Receive";
import { ManualInvoiceEditableFields } from "../../../components/Invoice/types";
import { ReceiveType } from "../../../util/receiveTypeMapping";
import { UserContext } from "../../../providers/user";

export const useInitialization = (receiveType: ReceiveType) => {
	const welcomeModalShownThisSession = useRef(false);
	const [initialised, setInitialised] = useState(false);
	const [pageContext] = useContext(PageContext);
	const { graphProfile } = useContext(UserContext);
	const purchaseOrderNumber = useQuery().get("purchaseOrderNumber") as string | undefined;
	const receiveId = useQuery().get("id") as string | undefined;
	const activeTabQueryString = useQuery().get("activeTab") as string | undefined;
	const invoiceNumber = useQuery().get("invoiceNumber") as string | undefined;
	const timestamp = useQuery().get("t");
	const selectedActiveTab = activeTabQueryString ? Number(activeTabQueryString) : 1;
	const [currentScannedProduct, setCurrentScannedProduct] = useState<CurrentScannedProduct>(emptyCurrentScannedProduct);
	const history = useHistory();
	const [{ data: pageData, loading: isLoading, error: apiError }, refreshPurchaseOrder] =
		useGetReceivePurchaseOrderDetails(purchaseOrderNumber ?? "", invoiceNumber ?? "", receiveId, true);
	const [modal, setModal] = useState<Modal>(modalClosed);

	useEffect(() => {
		// Fetch data on mount and when timestamp changes (e.g., navigating back to the page)
		refreshPurchaseOrder().catch(() => {
			setModal({
				...modals.genericError,
				primaryButtonName: "OK",
				title: "Unable to load purchase order details. Please try again",
				onPrimaryButtonClick: () => setModal(modalClosed),
				onExitClick: () => setModal(modalClosed),
			});
		});
	}, [refreshPurchaseOrder, timestamp, setModal]);

	const [activeTab, setActiveTab] = useState<number>(selectedActiveTab);
	const [isActionPrevented, setIsActionPrevented] = useState<boolean>(false);
	const [loadingDialog, setLoadingDialog] = useState<LoadingDialog>({
		isOpen: true,
		message: "Loading invoice details",
	});
	const [alertHeader, setAlertHeader] = useState<AlertState>(alerts.empty);

	const [receive, setReceive] = useState<Receive | undefined>(undefined);
	const [receives, setReceives] = useState<Receive[]>([]);

	const featureCheckConfig = useMemo(
		() => ({
			locationId: pageData?.purchaseOrder.shipToLocation.locationId,
			receiveType: receive?.receiveType ?? receiveType,
		}),
		[pageData?.purchaseOrder.shipToLocation.locationId, receive?.receiveType, receiveType]
	);

	const { isFeatureEnabled } = useFeatureCheck(featureCheckConfig);

	const productDetails = useMemo(() => {
		return pageData
			? getAdditionalProductDetails(pageData.products, pageContext.productGroups, pageData.productPrices)
			: [];
	}, [pageData, pageContext.productGroups]);

	const locationBays = useMemo(
		() => mapLocationBaysFromReceiveLineItems(receive?.receiveLineItems ?? [], ""),
		[receive?.receiveLineItems]
	);

	const purchaseOrderLineItems = useMemo(() => {
		if (!pageData) return [];

		let enrichedPoLineItems = mapPurchaseOrderLineItemsWithAdditionalDetails(
			pageData.purchaseOrder,
			receives,
			productDetails,
			pageData.customerOrders.inventory
		);

		if (isFeatureEnabled("priorityStock") || isFeatureEnabled("arrivals")) {
			sortItemsByPriorityStockFlag(enrichedPoLineItems, pageData?.priorityStock ?? []);
		}

		return enrichedPoLineItems;
	}, [isFeatureEnabled, pageData, receives, productDetails]);

	const canPurchaseOrderBeReopened = useMemo(
		() => isThereAnyPoLineAbleToBeReopened(purchaseOrderLineItems),
		[purchaseOrderLineItems]
	);

	const isInReview = useMemo(() => receives.some((r) => r.status === receiveStatus.reviewing), [receives]);

	const hasCustomerOrders = useMemo(
		() => (pageData?.customerOrders?.orders || []).length > 0,
		[pageData?.customerOrders?.orders]
	);

	const enrichedReceive = useMemo(
		() =>
			enrichReceiveForManualReceive(
				receive,
				pageData?.purchaseOrder,
				productDetails,
				receives.filter((r) => r.status !== receiveStatus.inProgress) ?? [],
				pageData?.customerOrders?.inventory
			),
		[receive, pageData?.purchaseOrder, productDetails, receives, pageData?.customerOrders?.inventory]
	);

	const [selectedCommercialAccount, setSelectedCommercialAccount] = useState<string>("");
	const [inboundLocation, setInboundLocation] = useState("");

	const [editableInvoiceFields, setEditableInvoiceFields] = useState<ManualInvoiceEditableFields>({
		invoiceDate: "",
		receivedDate: "",
		invoiceNumber: "",
		notes: "",
		totalInvoicedFreightEx: undefined,
		totalInvoicedFreightInc: undefined,
		totalInvoicedFreightTax: undefined,
	});
	const sanitiseFreightEx = (value: string | number | undefined) => {
		if (value === undefined || value === "") {
			return undefined;
		}

		const parsed = Number(value);

		if (!Number.isFinite(parsed) || parsed < 0) {
			return undefined;
		}

		return Math.round(parsed * 100) / 100;
	};

	const calculateFreightBreakdown = (value: string | number | undefined) => {
		const rate = pageContext.taxRate ?? 0;
		const ex = sanitiseFreightEx(value);
		const inc = ex === undefined ? undefined : Math.round(ex * (1 + rate) * 100) / 100;
		const tax = ex === undefined ? undefined : Math.round(ex * rate * 100) / 100;
		return { ex, inc, tax };
	};

	const applyFreightToReceive = <T extends Receive>(target: T, freight: ReturnType<typeof calculateFreightBreakdown>): T => ({
		...target,
		totalInvoicedFreightEx: freight.ex ?? 0,
		totalInvoicedFreightInc: freight.inc ?? 0,
		totalInvoicedFreightTax: freight.tax ?? 0,
	});

	const handleInvoiceFieldChanged = (
		field: keyof ManualInvoiceEditableFields,
		value: string | number | undefined
	) => {
		if (field === "totalInvoicedFreightEx") {
			const freight = calculateFreightBreakdown(value);

			setEditableInvoiceFields((prevState) => ({
				...prevState,
				totalInvoicedFreightEx: freight.ex,
				totalInvoicedFreightInc: freight.inc,
				totalInvoicedFreightTax: freight.tax,
			}));

			if (receive) {
				const updatedReceive = applyFreightToReceive(receive, freight);
				setReceive(updatedReceive);
				setReceives((prev) => addOrUpdateReceiveToReceivesList(prev, updatedReceive));
			}
			return;
		}

		setEditableInvoiceFields((prevState) => ({
			...prevState,
			[field]: value,
		}));

		if (receive) {
			const updatedReceive = {
				...receive,
				[field]: value,
			};
			setReceive(updatedReceive);
			setReceives((prev) => addOrUpdateReceiveToReceivesList(prev, updatedReceive));
		}
	};

	const handlePreviousReceiveFieldChanged = (receiveId: string, field: keyof Receive, value: any) => {
		setReceives((prev) => {
			const receive = prev.find((r) => r.id === receiveId);
			if (!receive) return prev;

			const receiveToUpdate =
				field === "totalInvoicedFreightEx"
					? applyFreightToReceive(receive, calculateFreightBreakdown(value))
					: { ...receive, [field]: value };

			return addOrUpdateReceiveToReceivesList(prev, receiveToUpdate);
		});
	};

	const ordersAlreadyAllocated = useMemo(() => hasReceiveAlreadyBeenAllocatedCustomerOrders(receive), [receive]);

	const hasLabels = useMemo(() => {
		return productDetails.some((pd) => pd.hasLabel);
	}, [productDetails]);

	const canCreateNewReceive = useMemo(() => {
		return !isActionPrevented && pageData?.purchaseOrder && pageData.purchaseOrder.shipToLocation;
	}, [isActionPrevented, pageData?.purchaseOrder]);

	const returnToHome = useCallback(() => {
		history.push("/");
	}, [history]);

	useEffect(() => {
		if ((!pageData || isLoading) && !apiError) {
			setLoadingDialog({ isOpen: true, message: "Loading invoice details..." });
			return;
		}

		if (apiError) {
			setModal({
				...modals.genericError,
				title: "Unable to load purchase order details",
				onPrimaryButtonClick: returnToHome,
				onExitClick: () => returnToHome,
			});
			setLoadingDialog(loadingDialogClosed);
			return;
		}

		setLoadingDialog(loadingDialogClosed);
	}, [pageData, isLoading, apiError, returnToHome]);

	useEffect(() => {
		if (!pageData || isLoading) return;

		if (receiveType !== "Manual") {
			// Manuals only set the in progress receive, see useModal
			setReceive(pageData.matchedReceive);
		}
		setReceives(pageData.receives);

		setInitialised(true);
	}, [graphProfile.staffId, pageData, isLoading, receiveType, selectedActiveTab]);

	useEffect(() => {
		setAlertHeader(
			hasReceiveAlreadyBeenAllocatedCustomerOrders(receive) ? alerts.cannotAllocateCustomerOrders : alerts.empty
		);

		setEditableInvoiceFields({
			invoiceNumber: receive?.invoiceNumber,
			receivedDate: receive?.receivedDate,
			invoiceDate: receive?.invoiceDate,
			notes: receive?.notes,
			totalInvoicedFreightEx: receive?.totalInvoicedFreightEx ? receive.totalInvoicedFreightEx : undefined,
			totalInvoicedFreightInc: receive?.totalInvoicedFreightInc ? receive.totalInvoicedFreightInc : undefined,
			totalInvoicedFreightTax: receive?.totalInvoicedFreightTax ? receive.totalInvoicedFreightTax : undefined,
		});

		setSelectedCommercialAccount(receive?.inventoryStatus ?? "");
	}, [receive]);

	return {
		pageData,
		activeTab,
		setActiveTab,
		receive,
		setReceive,
		receives,
		setReceives,
		originalReceives: pageData?.receives?.filter((r) => r.status !== receiveStatus.inProgress) ?? [],
		isActionPrevented,
		setIsActionPrevented,
		loadingDialog,
		setLoadingDialog,
		locationBays,
		modal,
		setModal,
		productDetails,
		hasCustomerOrders,
		alertHeader,
		setAlertHeader,
		canPurchaseOrderBeReopened,
		isInReview,
		purchaseOrderLineItems,
		isPartReceiveFeatureEnabled: isFeatureEnabled("partReceive"),
		enableAssignIbLocation: isFeatureEnabled("inboundLocationAssignment"),
		enableCommercialAccountSelection: isFeatureEnabled("commercialAccountSelection"),
		enableProductLabelAutoPrint: isFeatureEnabled("productLabelAutoPrint"),
		enabledPriorityStock: isFeatureEnabled("priorityStock"),
		enabledArrivals: isFeatureEnabled("arrivals"),
		captureFreightCostEnabled: isFeatureEnabled("captureFreightCost"),
		hasLabels,
		returnToHome,
		canCreateNewReceive,
		enrichedReceive,
		editableInvoiceFields,
		handleInvoiceFieldChanged,
		handlePreviousReceiveFieldChanged,
		ordersAlreadyAllocated,
		welcomeModalShownThisSession,
		initialised,
		currentScannedProduct,
		setCurrentScannedProduct,
		selectedCommercialAccount,
		setSelectedCommercialAccount,
		inboundLocation,
		setInboundLocation,
	};
};
